import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { PatchPriorRequest, PostPriorRequest, PriorDto, WorklistItemDetailsDto } from '../../../api-module/control-centre-api-client';
import { Observable, Subscription, TimeoutError, catchError, throwError } from 'rxjs';
import { EditablePriorDto } from '../details.models';
import { AbstractControl, FormArray, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { SettingsService } from '../../../services/settings.service';
import { Constants } from '../../../models/constants';
import { WorklistService } from '../../worklist.service';
import { SnackbarService } from '../../../services/snackbar.sevice';
import { MessageBoxService } from '../../../messagebox/services/messagebox.service';
import { MessageBoxButtons, MessageBoxIcon, MessageBoxResult } from '../../../messagebox/messagebox.model';
import moment from 'moment';
import { AuthenticationService } from 'src/app/authentication/services/authentication.service';
import { faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import { CommonUtilities } from '@app/shared/common-utilities';
import { FileUploadInfoErrorEnum } from '@app/shared/model/shared.model';
import { HttpErrorResponse, HttpEventType, HttpStatusCode } from '@angular/common/http';
import { FileUploadInfo } from '@app/shared/model/file-upload-info.model';
import { PostPriorReportRequest } from '@app/models/post-prior-report-request.model';

@Component({
    selector: 'app-priors',
    templateUrl: './priors.component.html',
    styleUrls: ['./priors.component.scss']
})
export class PriorsComponent implements OnInit, OnChanges, OnDestroy {
    @Input()
    item: WorklistItemDetailsDto;

    @Input()
    editable: boolean;

    @Output()
    priorUpdated = new EventEmitter<WorklistItemDetailsDto>();

    priorDataSource = new MatTableDataSource<PriorDto>();
    priorColumns: string[] = ['modality', 'dateOfStudy', 'accessionNumber'];
    modalityList$: Observable<string[]>;

    faPaperPlane = faPaperPlane;
    PriorsComponent = PriorsComponent;

    form: UntypedFormGroup;
    subscriptions = new Subscription();
    private readonly priorUploadBtns: Map<string, UploadButton> = new Map();
    priorSelectedForUpload: PriorDto;

    private static readonly acceptedFileMimeType = ['image/jpeg', 'image/png', 'image/gif', 'image/tiff', 'image/bmp', 'application/pdf'];
    private static readonly acceptedFileExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.tiff', '.bmp', '.pdf'];
    public static readonly acceptedFileExtensionsStr = PriorsComponent.acceptedFileExtensions.join(', ');
    private static readonly maxFileSize = 10 * 1024 * 1024;
    private static readonly formDataFileKey = 'File';
    private static readonly UNABLE_TO_UPLOAD_MESSAGE =
        'Please try again soon and if the problem persists please contact the Everlight Support Team';

    constructor(
        private readonly formBuilder: UntypedFormBuilder,
        private readonly settingsService: SettingsService,
        private readonly worklistService: WorklistService,
        private readonly snackbarService: SnackbarService,
        private readonly messageBoxService: MessageBoxService,
        private readonly authenticationService: AuthenticationService
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.item) {
            const priorControls = this.form?.controls.priors as FormArray;
            const resetPriors =
                changes.item.firstChange ||
                changes.item.previousValue['accessionNumber'] !== changes.item.currentValue['accessionNumber'] ||
                (priorControls?.length ?? 0) === 0;

            if (resetPriors) {
                // Create form from priors of updated order
                this.form = this.formBuilder.group({
                    priors: this.formBuilder.array(changes.item.currentValue.priors.map(prior => this.buildPriorControls(prior)))
                });
                this.priorDataSource.data = changes.item.currentValue.priors;
            } else if (priorControls) {
                const priors = this.priorDataSource.data as EditablePriorDto[];
                const currentPriors = changes.item.currentValue.priors as EditablePriorDto[];

                const editedPriors: EditablePriorDto[] = [];
                const newPriorControls: AbstractControl<unknown, unknown>[] = [];
                for (let index = 0; index < priorControls.length; index++) {
                    const prior = priors[index];
                    if (prior.isEditing) {
                        if (!prior.isNew && prior.priorId) {
                            editedPriors.push(prior);
                        } else {
                            newPriorControls.push(priorControls.at(index));
                        }
                    }
                }

                const editedPriorsMap = editedPriors
                    ? new Map(editedPriors.map(editedPrior => [editedPrior.priorId, editedPrior]))
                    : new Map<number, EditablePriorDto>();

                // Merge in any changes to edited existing priors
                const priorControlsToAdd = currentPriors.map(prior => {
                    const editedPrior = editedPriorsMap.get(prior.priorId);
                    if (editedPrior) {
                        const editedPriorControl = priorControls.controls.find(
                            priorControl => priorControl.value['priorId'] === prior.priorId
                        );
                        prior.accessionNumber = editedPriorControl.value['accessionNumber'];
                        prior.modality = editedPriorControl.value['modality'];
                        prior.studyDate = editedPriorControl.value['studyDate'];
                        prior.isEditing = true;
                        const control = this.buildPriorControls(prior);
                        control.markAsDirty();
                        return control;
                    }

                    return this.buildPriorControls(prior);
                });

                // Create form from priors of updated order, with changes
                this.form = this.formBuilder.group({
                    priors: this.formBuilder.array(priorControlsToAdd)
                });

                this.priorDataSource.data = currentPriors;

                // Add new priors
                newPriorControls?.forEach(newPriorControl => {
                    const newPrior = new EditablePriorDto(true, true, {
                        modality: newPriorControl.value['modality'],
                        studyDate: newPriorControl.value['studyDate'],
                        accessionNumber: newPriorControl.value['accessionNumber']
                    });
                    const addedRow = this.addPriorRow(newPrior);
                    addedRow.markAsDirty();
                });
            }

            this.priorDataSource._updateChangeSubscription();
        }
    }

    ngOnInit(): void {
        if (this.editable) {
            this.priorColumns.push('actions');
        }
        this.modalityList$ = this.settingsService.getModalities();
        this.priorDataSource.data = this.item.priors;
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    private buildPriorControls(prior: PriorDto): UntypedFormGroup {
        return this.formBuilder.group({
            priorId: [prior?.priorId],
            modality: [prior?.modality, [Validators.required]],
            studyDate: [prior?.studyDate ? moment(prior.studyDate) : null, [Validators.required]],
            accessionNumber: [prior?.accessionNumber, [Validators.maxLength(50), Validators.pattern(Constants.AccessionNumberPattern)]]
        });
    }

    getPriorFormGroup(i: number) {
        return (this.form.controls.priors as UntypedFormArray).controls[i] as UntypedFormGroup;
    }

    getPriorControls(i: number) {
        return this.getPriorFormGroup(i)?.controls;
    }

    addNewPriorRow() {
        this.addPriorRow(new EditablePriorDto(true, true));
        this.priorDataSource._updateChangeSubscription();
    }

    addPriorRow(prior: EditablePriorDto): UntypedFormGroup {
        const priorControl = this.buildPriorControls(prior);
        (this.form.controls.priors as UntypedFormArray).push(priorControl);
        this.priorDataSource.data.push(prior);
        return priorControl;
    }

    editPriorRow(row, index) {
        const itemToEdit = row as EditablePriorDto;
        itemToEdit.isEditing = true;
        const controlGroup = this.getPriorControls(index);
        controlGroup.priorId.setValue(itemToEdit.priorId);
        controlGroup.modality.setValue(itemToEdit.modality);
        controlGroup.studyDate.setValue(moment(itemToEdit.studyDate));
        controlGroup.accessionNumber.setValue(itemToEdit.accessionNumber);

        this.priorDataSource.data[index] = itemToEdit;
        this.priorDataSource._updateChangeSubscription();
    }

    async savePriorRow(row, index) {
        const controlGroup = this.getPriorFormGroup(index);
        controlGroup.markAllAsTouched();
        if (controlGroup.valid) {
            try {
                const modality = controlGroup.controls?.modality.value;
                const priorDate = controlGroup.controls?.studyDate.value.format('YYYY-MM-DD');
                const accessionNumber = controlGroup.controls?.accessionNumber.value;
                const radiographerName = this.item.radiographerName ? null : this.authenticationService.getCurrentUser()?.username;
                controlGroup.disable();
                if (row.isNew) {
                    row = await this.worklistService.createPrior(
                        this.item.id,
                        new PostPriorRequest({
                            modality,
                            priorDate,
                            accessionNumber,
                            radiographerName
                        })
                    );
                    this.snackbarService.openSnackBar('Added', SnackbarService.SNACK_BAR_CLASS_SUCCESS);
                } else {
                    row = await this.worklistService.patchPrior(
                        this.item.id,
                        row.priorId,
                        new PatchPriorRequest({
                            modality,
                            priorDate,
                            accessionNumber,
                            radiographerName
                        })
                    );
                    this.item.radiographerName = radiographerName;
                    this.snackbarService.openSnackBar('Updated', SnackbarService.SNACK_BAR_CLASS_SUCCESS);
                }

                this.priorDataSource.data[index] = row;
                this.priorDataSource._updateChangeSubscription();
                controlGroup.markAsPristine();
                this.priorUpdated.emit(this.item);
            } catch (e) {
                this.snackbarService.openSnackBar(Constants.UnableToContactServerMessage, SnackbarService.SNACK_BAR_CLASS_ALERT);
                throw e;
            } finally {
                controlGroup.enable();
            }
        }
    }

    async deletePriorRow(row, index) {
        try {
            if (!row.isNew) {
                const title = 'Confirmation';
                const message = 'Are you sure to delete this prior record?';
                const result = await this.messageBoxService
                    .showMessageBox({
                        title,
                        message,
                        information: null,
                        icon: MessageBoxIcon.Confirmation,
                        buttons: MessageBoxButtons.YesNo
                    })
                    .toPromise();
                if (result === MessageBoxResult.No) {
                    return;
                }
                const radiographerName = this.item.radiographerName ? null : this.authenticationService.getCurrentUser()?.username;
                await this.worklistService.deletePrior(this.item.id, row.priorId, radiographerName);
                this.snackbarService.openSnackBar('Deleted', SnackbarService.SNACK_BAR_CLASS_SUCCESS);

                this.item.radiographerName = radiographerName ? this.item.radiographerName : radiographerName;
            }

            (this.form.controls.priors as UntypedFormArray).removeAt(index);
            this.priorDataSource.data.splice(index, 1);
            this.priorDataSource._updateChangeSubscription();
            this.priorUpdated.emit(this.item);
        } catch (e) {
            this.snackbarService.openSnackBar(Constants.UnableToContactServerMessage, SnackbarService.SNACK_BAR_CLASS_ALERT);
            throw e;
        }
    }

    getUploadBtnClass(priorId: string | number): PriorUploadBtnClass {
        const buttonId = PriorsComponent.getUploadButtonIdFromPriorId(priorId);
        return this.priorUploadBtns.get(buttonId)?.class ?? '';
    }

    getUploadBtnText(priorId: string | number): PriorUploadBtnMessage {
        const buttonId = PriorsComponent.getUploadButtonIdFromPriorId(priorId);
        return this.priorUploadBtns.get(buttonId)?.displayMessage ?? 'Send Report';
    }

    isUploadButtonDisabled(prior: PriorDto): boolean {
        const button = this.priorUploadBtns.get(PriorsComponent.getUploadButtonIdFromPriorId(prior?.priorId));
        const isButtonInDefaultState = !button || button.status === 'Initial';
        return !prior?.priorId || !this.editable || !isButtonInDefaultState;
    }

    static getUploadButtonIdFromPriorId(priorId: string | number | undefined): string {
        return 'upload-btn-' + priorId;
    }

    initiateFileUpload(_: () => void, prior: PriorDto) {
        const buttonId = PriorsComponent.getUploadButtonIdFromPriorId(prior.priorId);
        const matchedPriorButton = this.priorUploadBtns.get(buttonId);
        if (!matchedPriorButton) {
            this.priorUploadBtns.set(
                buttonId,
                new UploadButton({
                    displayMessage: 'Send Report',
                    status: 'Initial'
                })
            );
        }
        this.priorSelectedForUpload = prior;
    }

    onFileSelected(event: Event) {
        const eventTarget = event.target as HTMLInputElement;
        Array.from(eventTarget.files).forEach(file => this.uploadFile(file, this.priorSelectedForUpload));
        // resets the value of file input so the same file can be uploaded again (onChange wouldn't fire for same file)
        const fileInput = event.target as HTMLInputElement;
        fileInput.value = '';
    }

    private uploadFile(fileToUpload: File, prior: PriorDto) {
        if (fileToUpload) {
            const priorReport = FileUploadInfo.generateFileUploadInfo(fileToUpload);
            const buttonId = PriorsComponent.getUploadButtonIdFromPriorId(prior?.priorId);
            const button = this.priorUploadBtns.get(buttonId);

            if (this.isFileValid(fileToUpload, priorReport)) {
                button?.setToInProgress();
                const request = new PostPriorReportRequest({
                    file: fileToUpload,
                    priorAccessionNumber: prior?.accessionNumber,
                    priorModality: prior?.modality,
                    priorStudyDate: prior?.studyDate
                });

                const uploadSubscription = this.worklistService
                    .uploadPriorReport(this.item.accessionNumber, request)
                    .pipe(catchError(this.getErrorMessage))
                    .subscribe({
                        next: event => {
                            if (event.type === HttpEventType.Response && event.status === HttpStatusCode.Accepted) {
                                button?.setToSuccess();
                                this.snackbarService.openSnackBarNoAutoDismiss(
                                    'Priors Report sent to Everlight Radiology',
                                    SnackbarService.SNACK_BAR_CLASS_SUCCESS
                                );
                                this.revertUploadPriorReportButton(buttonId);
                            }
                        },
                        error: (err: Error) => {
                            button?.setToFail();
                            this.promptErrorMessage(priorReport, FileUploadInfoErrorEnum.onUpload, err.message);
                            this.revertUploadPriorReportButton(buttonId);
                        }
                    });
                this.subscriptions.add(uploadSubscription);
            }
        }
    }

    private isFileValid(uploadedFile: File, fileInfo: FileUploadInfo): boolean {
        if (!PriorsComponent.acceptedFileMimeType.find(fileType => fileType === uploadedFile.type)) {
            this.promptErrorMessage(fileInfo, FileUploadInfoErrorEnum.fileType);
        } else if (uploadedFile.size > PriorsComponent.maxFileSize) {
            this.promptErrorMessage(fileInfo, FileUploadInfoErrorEnum.fileSize);
        } else {
            return true;
        }
        return false;
    }

    private getErrorMessage(response: HttpErrorResponse | TimeoutError) {
        if (response instanceof TimeoutError) {
            console.error('Request timeout: ', JSON.stringify(response));
            return throwError(() => new Error(Constants.UnableToContactServerMessage));
        } else if (
            response instanceof HttpErrorResponse &&
            (response.error instanceof ProgressEvent || response.status === 502 || response.status === 503 || response.status === 504)
        ) {
            console.error('Unable to contact server: ', response.status);
            return throwError(() => new Error(Constants.UnableToContactServerMessage));
        }
        console.error(`Server returned code ${response.status} with body:\n${JSON.stringify(response.error)}`);
        if (response.status === 403) {
            return throwError(() => new Error('You do not have permission to upload the documents'));
        }
        if (response.status === 400) {
            // SPA shouldn't submit an invalid form. if it does and server returns error, it's an error scenario.
            const errorMessage =
                response.error?.errors?.[PriorsComponent.formDataFileKey]?.toLocaleString() ?? PriorsComponent.UNABLE_TO_UPLOAD_MESSAGE;
            return throwError(() => new Error(errorMessage));
        }
        return throwError(() => new Error(PriorsComponent.UNABLE_TO_UPLOAD_MESSAGE));
    }

    private promptErrorMessage(fileInfo: FileUploadInfo, errorType: FileUploadInfoErrorEnum, errorMessage?: string) {
        const title = 'Prior Report Send Error';
        let message;
        const information = `Sending "${fileInfo.name}" has failed.`;

        switch (errorType) {
            case 'onUpload':
                message = errorMessage ?? 'The file could not be sent, please try to send again in a few minutes.';
                break;
            case 'fileSize': {
                const sizeLimitMB = CommonUtilities.bytesToMegabytes(PriorsComponent.maxFileSize);
                message = errorMessage ?? `Only files less than ${sizeLimitMB}MB in size can be attached.`;
                break;
            }
            case 'fileType':
                message =
                    errorMessage ??
                    `The file is in ${fileInfo.extension} format. Only ${PriorsComponent.acceptedFileExtensionsStr} files can be attached.`;
                break;
            default:
                return;
        }

        this.subscriptions.add(
            this.messageBoxService
                .showMessageBox({
                    title,
                    message,
                    information,
                    icon: MessageBoxIcon.Warning,
                    buttons: MessageBoxButtons.Ok,
                    allowMultiplePopup: true
                })
                .subscribe()
        );
    }

    private revertUploadPriorReportButton(buttonId: string) {
        const revertButtonInMilliSeconds = 5000;
        setInterval(() => {
            this.priorUploadBtns.get(buttonId)?.setToDefault();
        }, revertButtonInMilliSeconds);
    }

    dateFilter = (date: Date): boolean => date <= new Date();

    formatStudyDate(studyDate: string): string {
        if (studyDate) {
            return moment(studyDate).format('DD MMM YYYY');
        } else {
            return null;
        }
    }
}

type PriorUploadBtnStatus = 'Initial' | 'In Progress' | 'Success' | 'Error';
type PriorUploadBtnClass = '' | 'sending' | 'sent' | 'fail';
type PriorUploadBtnMessage = 'Send Report' | 'Sending...' | 'Report Sent' | 'Report Fail';
export class UploadButton {
    constructor(data: { status: PriorUploadBtnStatus; displayMessage: PriorUploadBtnMessage }) {
        this.status = data.status;
        this.displayMessage = data.displayMessage;
    }

    status: PriorUploadBtnStatus;
    displayMessage: PriorUploadBtnMessage;
    class: PriorUploadBtnClass;

    setToInProgress() {
        this.status = 'In Progress';
        this.displayMessage = 'Sending...';
        this.class = UploadButton.mapStatusToClass(this.status);
    }

    setToDefault() {
        this.status = 'Initial';
        this.displayMessage = 'Send Report';
        this.class = UploadButton.mapStatusToClass(this.status);
    }

    setToSuccess() {
        this.status = 'Success';
        this.displayMessage = 'Report Sent';
        this.class = UploadButton.mapStatusToClass(this.status);
    }

    setToFail() {
        this.status = 'Error';
        this.displayMessage = 'Report Fail';
        this.class = UploadButton.mapStatusToClass(this.status);
    }

    private static mapStatusToClass(status: PriorUploadBtnStatus): PriorUploadBtnClass {
        switch (status) {
            case 'Initial':
                return '';
            case 'In Progress':
                return 'sending';
            case 'Success':
                return 'sent';
            case 'Error':
                return 'fail';
        }
    }
}
